import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Table, Card, Tag, Space, Input, Select, DatePicker, Button, Modal, Descriptions, message, Popconfirm, Spin } from 'antd';
import {
  SearchOutlined,
  EyeOutlined,
  DollarOutlined,
  RollbackOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { apiClient } from '../../lib/api-client';

const { RangePicker } = DatePicker;

interface Payment {
  id: number;
  clientId: number;
  tripType: string;
  tripId: number;
  amount: number;
  bonusUsed: number;
  promoDiscount: number;
  finalAmount: number;
  paymentMethod: string;
  status: string;
  externalId: string | null;
  createdAt: string;
  completedAt: string | null;
  client: {
    id: number;
    fullName: string;
    phone: string;
  };
}

interface Transaction {
  id: number;
  transactionType: string;
  amount: number;
  description: string | null;
  timestamp: string;
  fromUser?: { fullName: string; phone: string };
  toUser?: { fullName: string; phone: string };
}

const statusLabels: Record<string, string> = {
  PENDING: 'Kutilmoqda',
  PROCESSING: 'Jarayonda',
  COMPLETED: 'Bajarildi',
  FAILED: 'Xato',
  REFUNDED: 'Qaytarildi',
};

const statusColors: Record<string, string> = {
  PENDING: 'orange',
  PROCESSING: 'blue',
  COMPLETED: 'green',
  FAILED: 'red',
  REFUNDED: 'purple',
};

const paymentMethodLabels: Record<string, string> = {
  CASH: 'Naqd',
  UZCARD: 'UzCard',
  HUMO: 'Humo',
};

const tripTypeLabels: Record<string, string> = {
  INTERCITY_TAXI: 'Shahar ichida taksi',
  INTERREGION_TAXI: 'Shaharlararo taksi',
  POSTAGE: 'Pochta',
  CARGO: 'Yuk tashish',
};

const transactionTypeLabels: Record<string, string> = {
  TRIP_PAYMENT: 'Sayohat to\'lovi',
  COMMISSION: 'Komissiya',
  BONUS_CREDIT: 'Bonus qo\'shildi',
  BONUS_DEBIT: 'Bonus ishlatildi',
  REFUND: 'Qaytarish',
  REFERRAL_BONUS: 'Referal bonus',
  INCENTIVE_BONUS: 'Rag\'batlantirish bonusi',
  WALLET_TOPUP: 'Hamyon to\'ldirish',
  WALLET_WITHDRAWAL: 'Hamyondan yechish',
};

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('uz-UZ').format(value) + ' so\'m';
};

export const PaymentList = () => {
  const location = useLocation();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string | undefined>();
  const [paymentMethod, setPaymentMethod] = useState<string | undefined>();
  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [refunding, setRefunding] = useState(false);

  const [payments, setPayments] = useState<Payment[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  // Detect if we're on bus section
  const isBusSection = location.pathname.startsWith('/bus/');
  const serviceType = isBusSection ? 'bus' : 'taxi';

  const fetchPayments = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get('/admin/payments', {
        params: {
          search: search || undefined,
          status: status || undefined,
          paymentMethod: paymentMethod || undefined,
          startDate: dateRange?.[0]?.format('YYYY-MM-DD'),
          endDate: dateRange?.[1]?.format('YYYY-MM-DD'),
          page,
          limit: pageSize,
          serviceType,
        },
      });
      setPayments(response.data?.data || []);
      setTotal(response.data?.meta?.total || 0);
    } catch (error) {
      message.error('To\'lovlarni yuklashda xatolik');
      setPayments([]);
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, [page, pageSize]);

  const refetch = () => fetchPayments();

  const handleViewDetails = async (payment: Payment) => {
    setSelectedPayment(payment);
    setDetailsModalOpen(true);
    setLoadingTransactions(true);

    try {
      const response = await apiClient.get(`/admin/payments/${payment.id}/transactions`);
      setTransactions(response.data);
    } catch {
      message.error('Tranzaksiyalarni yuklashda xatolik');
    } finally {
      setLoadingTransactions(false);
    }
  };

  const handleRefund = async (paymentId: number) => {
    setRefunding(true);
    try {
      await apiClient.post(`/admin/payments/${paymentId}/refund`);
      message.success('To\'lov qaytarildi');
      setDetailsModalOpen(false);
      refetch();
    } catch {
      message.error('Qaytarishda xatolik');
    } finally {
      setRefunding(false);
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Mijoz',
      key: 'client',
      render: (_: unknown, record: Payment) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.client?.fullName || 'Ismi yo\'q'}</div>
          <div style={{ fontSize: 12, color: '#7F8C8D' }}>{record.client?.phone}</div>
        </div>
      ),
    },
    {
      title: 'Sayohat turi',
      dataIndex: 'tripType',
      key: 'tripType',
      render: (type: string) => (
        <Tag>{tripTypeLabels[type] || type}</Tag>
      ),
    },
    {
      title: 'Summa',
      key: 'amount',
      render: (_: unknown, record: Payment) => (
        <div>
          <div style={{ fontWeight: 500 }}>{formatCurrency(record.finalAmount)}</div>
          {(record.bonusUsed > 0 || record.promoDiscount > 0) && (
            <div style={{ fontSize: 12, color: '#7F8C8D' }}>
              {record.bonusUsed > 0 && `Bonus: -${formatCurrency(record.bonusUsed)}`}
              {record.bonusUsed > 0 && record.promoDiscount > 0 && ', '}
              {record.promoDiscount > 0 && `Promo: -${formatCurrency(record.promoDiscount)}`}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'To\'lov usuli',
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      render: (method: string) => (
        <Tag color="blue">{paymentMethodLabels[method] || method}</Tag>
      ),
    },
    {
      title: 'Holat',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={statusColors[status] || 'default'}>
          {statusLabels[status] || status}
        </Tag>
      ),
    },
    {
      title: 'Sana',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => dayjs(date).format('DD.MM.YYYY HH:mm'),
    },
    {
      title: 'Amallar',
      key: 'actions',
      width: 100,
      render: (_: unknown, record: Payment) => (
        <Space>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record)}
            title="Ko'rish"
          />
        </Space>
      ),
    },
  ];

  const transactionColumns = [
    {
      title: 'Turi',
      dataIndex: 'transactionType',
      key: 'transactionType',
      render: (type: string) => transactionTypeLabels[type] || type,
    },
    {
      title: 'Summa',
      dataIndex: 'amount',
      key: 'amount',
      render: (val: number) => formatCurrency(val),
    },
    {
      title: 'Kimdan',
      key: 'fromUser',
      render: (_: unknown, record: Transaction) => record.fromUser?.fullName || '-',
    },
    {
      title: 'Kimga',
      key: 'toUser',
      render: (_: unknown, record: Transaction) => record.toUser?.fullName || '-',
    },
    {
      title: 'Vaqt',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (date: string) => dayjs(date).format('DD.MM.YYYY HH:mm:ss'),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ marginBottom: 24 }}>
        <DollarOutlined style={{ marginRight: 8 }} />
        To'lovlar
      </h2>

      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input
            placeholder="Qidirish (telefon, ID)"
            prefix={<SearchOutlined />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 200 }}
            allowClear
          />
          <Select
            placeholder="Holat"
            value={status}
            onChange={setStatus}
            style={{ width: 150 }}
            allowClear
            options={[
              { value: 'PENDING', label: 'Kutilmoqda' },
              { value: 'PROCESSING', label: 'Jarayonda' },
              { value: 'COMPLETED', label: 'Bajarildi' },
              { value: 'FAILED', label: 'Xato' },
              { value: 'REFUNDED', label: 'Qaytarildi' },
            ]}
          />
          <Select
            placeholder="To'lov usuli"
            value={paymentMethod}
            onChange={setPaymentMethod}
            style={{ width: 150 }}
            allowClear
            options={[
              { value: 'CASH', label: 'Naqd' },
              { value: 'UZCARD', label: 'UzCard' },
              { value: 'HUMO', label: 'Humo' },
            ]}
          />
          <RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates as [Dayjs, Dayjs] | null)}
            format="YYYY-MM-DD"
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={fetchPayments}>
            Qidirish
          </Button>
        </Space>
      </Card>

      <Card>
        <Table
          dataSource={payments}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={{
            current: page,
            pageSize,
            total,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
            showSizeChanger: true,
            showTotal: (total) => `Jami: ${total}`,
          }}
        />
      </Card>

      {/* Payment Details Modal */}
      <Modal
        title={
          <Space>
            <DollarOutlined />
            <span>To'lov #{selectedPayment?.id}</span>
          </Space>
        }
        open={detailsModalOpen}
        onCancel={() => {
          setDetailsModalOpen(false);
          setSelectedPayment(null);
          setTransactions([]);
        }}
        footer={
          selectedPayment?.status === 'COMPLETED' ? (
            <Popconfirm
              title="To'lovni qaytarishni tasdiqlaysizmi?"
              onConfirm={() => selectedPayment && handleRefund(selectedPayment.id)}
            >
              <Button
                danger
                icon={<RollbackOutlined />}
                loading={refunding}
              >
                Qaytarish
              </Button>
            </Popconfirm>
          ) : null
        }
        width={800}
      >
        {selectedPayment && (
          <div>
            <Descriptions bordered column={2} size="small" style={{ marginBottom: 24 }}>
              <Descriptions.Item label="Mijoz">
                {selectedPayment.client?.fullName || 'Ismi yo\'q'} ({selectedPayment.client?.phone})
              </Descriptions.Item>
              <Descriptions.Item label="Sayohat turi">
                {tripTypeLabels[selectedPayment.tripType] || selectedPayment.tripType}
              </Descriptions.Item>
              <Descriptions.Item label="Sayohat ID">
                #{selectedPayment.tripId}
              </Descriptions.Item>
              <Descriptions.Item label="To'lov usuli">
                {paymentMethodLabels[selectedPayment.paymentMethod] || selectedPayment.paymentMethod}
              </Descriptions.Item>
              <Descriptions.Item label="Boshlang'ich summa">
                {formatCurrency(selectedPayment.amount)}
              </Descriptions.Item>
              <Descriptions.Item label="Bonus ishlatildi">
                {formatCurrency(selectedPayment.bonusUsed)}
              </Descriptions.Item>
              <Descriptions.Item label="Promo chegirma">
                {formatCurrency(selectedPayment.promoDiscount)}
              </Descriptions.Item>
              <Descriptions.Item label="Yakuniy summa">
                <strong>{formatCurrency(selectedPayment.finalAmount)}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Holat">
                <Tag color={statusColors[selectedPayment.status] || 'default'}>
                  {statusLabels[selectedPayment.status] || selectedPayment.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Tashqi ID">
                {selectedPayment.externalId || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="Yaratilgan">
                {dayjs(selectedPayment.createdAt).format('DD.MM.YYYY HH:mm:ss')}
              </Descriptions.Item>
              <Descriptions.Item label="Bajarilgan">
                {selectedPayment.completedAt
                  ? dayjs(selectedPayment.completedAt).format('DD.MM.YYYY HH:mm:ss')
                  : '-'}
              </Descriptions.Item>
            </Descriptions>

            <h4 style={{ marginBottom: 16 }}>Tranzaksiya tarixi</h4>
            <Table
              dataSource={transactions}
              columns={transactionColumns}
              rowKey="id"
              loading={loadingTransactions}
              pagination={false}
              size="small"
            />
          </div>
        )}
      </Modal>
    </div>
  );
};
